import { CoverVideoPopover } from "@components/CoverVideoPopover";
import { ErrorMessage } from "@components/ErrorMessage";
import { TextInput } from "@components/TextInput";
import React, { SetStateAction, Dispatch, useRef } from "react";

import { extractYouTubeVideoId } from "@every.org/common/src/helpers/socialNetworks";

import {
  StyledAlternativeRadioInput,
  textInputBoxResetStyles,
} from "src/components/CoverPhotoOrVideoPicker/styles";
import {
  CoverPhotoOrVideoPickerProps,
  CoverOption,
} from "src/components/CoverPhotoOrVideoPicker/types";
import { colorCssVars } from "src/theme/color";
import { horizontalStackCss, verticalStackCss } from "src/theme/spacing";

export const SelectCover = ({
  coverYoutubeVideoId,
  setCoverYoutubeVideoId,
  validationErrors,
  setValidationErrors,
  coverOption,
  setCoverOption,
  allowDefaultCoverImage,
}: CoverPhotoOrVideoPickerProps & {
  coverOption: CoverOption;
  setCoverOption: Dispatch<SetStateAction<CoverOption>>;
}) => {
  const uploadCoverVideoRef = useRef<HTMLInputElement>(null);
  return (
    <div css={verticalStackCss.m}>
      <fieldset css={verticalStackCss.s}>
        <StyledAlternativeRadioInput
          data-tname="custom-cover-photo"
          id="custom-cover-photo"
          checked={coverOption === CoverOption.PHOTO}
          onChange={() => setCoverOption(CoverOption.PHOTO)}
          label="Upload your own photo"
        />
        <StyledAlternativeRadioInput
          data-tname="custom-cover-video"
          id="custom-cover-video"
          checked={coverOption === CoverOption.VIDEO}
          onClick={() => uploadCoverVideoRef.current?.focus()}
          onChange={() => setCoverOption(CoverOption.VIDEO)}
          labelCss={[horizontalStackCss.s, { width: "100%" }]}
          label={
            <React.Fragment>
              {coverOption === CoverOption.VIDEO ? (
                <TextInput
                  id="coverYoutubeVideoUrl"
                  name="coverYoutubeVideoUrl"
                  ref={uploadCoverVideoRef}
                  data-tname="coverYoutubeVideoUrl"
                  inputBoxCss={textInputBoxResetStyles}
                  value={coverYoutubeVideoId || ""}
                  onChange={(e) => {
                    setValidationErrors((prev) => ({
                      ...prev,
                      coverYoutubeVideoUrl: undefined,
                    }));
                    setCoverYoutubeVideoId(
                      extractYouTubeVideoId(e.target.value)
                    );
                  }}
                  inputPrefix={
                    <div css={horizontalStackCss.s}>youtube.com/embed/</div>
                  }
                  css={{ width: "100%" }}
                  collapseDescriptionSpace
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus={coverOption === CoverOption.VIDEO}
                />
              ) : (
                <span css={{ width: "100%" }}>
                  Paste your YouTube video URL
                </span>
              )}
              <CoverVideoPopover />
            </React.Fragment>
          }
        />
        {validationErrors?.coverYoutubeVideoUrl?.message && (
          <ErrorMessage>
            {validationErrors.coverYoutubeVideoUrl.message}
          </ErrorMessage>
        )}
        {allowDefaultCoverImage && (
          <StyledAlternativeRadioInput
            data-tname="default-cover"
            id="default-cover"
            checked={coverOption === CoverOption.DEFAULT}
            onChange={() => setCoverOption(CoverOption.DEFAULT)}
            label="Use default cover photo"
          />
        )}
        {validationErrors?.hasCoverOption?.message && (
          <ErrorMessage>{validationErrors.hasCoverOption.message}</ErrorMessage>
        )}
      </fieldset>
      <p css={{ color: `var(${colorCssVars.text.secondary})` }}>
        Choose a photo without text or icons (text can go in the About section
        below). Photos should have a width to height ratio of about 2:1 with a
        size of at least 1900 x 950 pixels.
      </p>
    </div>
  );
};
